import React, { useState, useEffect, useMemo } from 'react';
import { FilterGroup, SettingItem, Currency } from '../types';
import { SearchableSelect } from './SearchableSelect';
import { CloseIcon } from './icons/CloseIcon';
import { BusinessItem } from '../contexts/SettingsContext';

type FilterableKeys = 'tags' | 'businessNames' | 'categories' | 'paymentMethods' | 'currencies';

export type FiltersState = Record<FilterableKeys, FilterGroup>;

interface FilterPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (newFilters: FiltersState) => void;
    initialFilters: FiltersState;
    filterOptions: {
        businessNames: ReadonlyArray<BusinessItem>;
        categories: ReadonlyArray<SettingItem>;
        paymentMethods: ReadonlyArray<SettingItem>;
        tags: ReadonlyArray<SettingItem>;
        currencies: ReadonlyArray<Currency>;
    };
}

const FilterSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="space-y-3 border-b border-gray-200 pb-4 mb-4">
        <h4 className="font-semibold text-gray-800">{title}</h4>
        {children}
    </div>
);

export const FilterPanel: React.FC<FilterPanelProps> = ({ isOpen, onClose, onApply, initialFilters, filterOptions }) => {
    const [localFilters, setLocalFilters] = useState<FiltersState>(initialFilters);

    useEffect(() => {
        if (isOpen) {
            setLocalFilters(initialFilters);
        }
    }, [isOpen, initialFilters]);

    const handleFilterChange = (key: FilterableKeys, type: 'include' | 'exclude', value: { name: string }[]) => {
        setLocalFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [type]: value.map(v => v.name)
            }
        }));
    };
    
    const handleApply = () => {
        onApply(localFilters);
        onClose();
    };

    const handleClear = () => {
        const clearedFilters: FiltersState = {
            tags: { include: [], exclude: [] },
            businessNames: { include: [], exclude: [] },
            categories: { include: [], exclude: [] },
            paymentMethods: { include: [], exclude: [] },
            currencies: { include: [], exclude: [] },
        };
        setLocalFilters(clearedFilters);
    };

    const hasActiveFilters = useMemo(() => {
        // FIX: Add explicit type for `group` to prevent it from being inferred as `unknown`.
        return Object.values(localFilters).some((group: FilterGroup) => group.include.length > 0 || group.exclude.length > 0);
    }, [localFilters]);

    return (
        <>
            <div 
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
                aria-hidden="true"
            />
            <div className={`fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <header className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-xl font-semibold text-gray-800">Filtry</h3>
                    <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-800">
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </header>

                <div className="flex-grow p-4 overflow-y-auto">
                    <FilterSection title="Kategorie">
                        <div>
                            <label className="text-xs font-medium text-gray-600 block mb-1">Musí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.categories.include.map(name => ({name}))} onChange={(v) => handleFilterChange('categories', 'include', v as SettingItem[])} options={filterOptions.categories} placeholder="Vyberte kategorie..." />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 block mb-1">Nesmí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.categories.exclude.map(name => ({name}))} onChange={(v) => handleFilterChange('categories', 'exclude', v as SettingItem[])} options={filterOptions.categories} placeholder="Vyloučit kategorie..." />
                        </div>
                    </FilterSection>

                    <FilterSection title="Štítky">
                        <div>
                            <label className="text-xs font-medium text-gray-600 block mb-1">Musí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.tags.include.map(name => ({name}))} onChange={(v) => handleFilterChange('tags', 'include', v as SettingItem[])} options={filterOptions.tags} placeholder="Vyberte štítky..." />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 block mb-1">Nesmí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.tags.exclude.map(name => ({name}))} onChange={(v) => handleFilterChange('tags', 'exclude', v as SettingItem[])} options={filterOptions.tags} placeholder="Vyloučit štítky..." />
                        </div>
                    </FilterSection>
                    
                    <FilterSection title="Podnik">
                         <div>
                            <label className="text-xs font-medium text-gray-600 block mb-1">Musí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.businessNames.include} onChange={(v) => handleFilterChange('businessNames', 'include', v as {name: string}[])} options={filterOptions.businessNames} placeholder="Vyberte podniky..."/>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 block mb-1">Nesmí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.businessNames.exclude} onChange={(v) => handleFilterChange('businessNames', 'exclude', v as {name: string}[])} options={filterOptions.businessNames} placeholder="Vyloučit podniky..."/>
                        </div>
                    </FilterSection>
                    
                     <FilterSection title="Způsob platby">
                         <div>
                            <label className="text-xs font-medium text-gray-600 block mb-1">Musí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.paymentMethods.include.map(name=>({name}))} onChange={(v) => handleFilterChange('paymentMethods', 'include', v as SettingItem[])} options={filterOptions.paymentMethods} placeholder="Vyberte platby..." />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 block mb-1">Nesmí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.paymentMethods.exclude.map(name=>({name}))} onChange={(v) => handleFilterChange('paymentMethods', 'exclude', v as SettingItem[])} options={filterOptions.paymentMethods} placeholder="Vyloučit platby..." />
                        </div>
                    </FilterSection>

                    <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Měna</h4>
                         <div>
                            <label className="text-xs font-medium text-gray-600 block mb-1">Musí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.currencies.include.map(name=>({name}))} onChange={(v) => handleFilterChange('currencies', 'include', v as Currency[])} options={filterOptions.currencies} placeholder="Vyberte měny..." />
                        </div>
                        <div className="mt-3">
                            <label className="text-xs font-medium text-gray-600 block mb-1">Nesmí obsahovat</label>
                            <SearchableSelect isMulti value={localFilters.currencies.exclude.map(name=>({name}))} onChange={(v) => handleFilterChange('currencies', 'exclude', v as Currency[])} options={filterOptions.currencies} placeholder="Vyloučit měny..." />
                        </div>
                    </div>

                </div>

                <footer className="flex items-center justify-between p-4 border-t bg-gray-50">
                    <button onClick={handleClear} disabled={!hasActiveFilters} className="text-sm text-gray-600 hover:underline disabled:opacity-50">Vymazat vše</button>
                    <button onClick={handleApply} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Použít</button>
                </footer>
            </div>
        </>
    );
};
